import { Spinner } from '@monksflow/monks-ui';
import { useParams, useNavigate } from 'react-router';
import { Suspense, lazy } from 'react';

import { useQuery } from '@tanstack/react-query';
import { analysisQuery } from '@/entities/analysis';
import { usePersonaById } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { SectionLayout } from '../../components';
import { StartNewMenu } from './components';
import { IQSuggestedAudienceDetail } from './IQSuggestedAudienceDetail';
import { useStartNewActions, useStartPersonaChat } from './lib';
import { toAudienceDraftCards } from './lib/audienceDrafts';
import { useTranslation } from 'react-i18next';

const PersonaDetailV2 = lazy(() =>
  import('@/features/audience-flow-widget').then((m) => ({ default: m.PersonaDetailV2 })),
);

export const IQPersonaDetail = () => {
  const { t } = useTranslation('analysis');

  const navigate = useNavigate();
  const { analysisId, personaId } = useParams<{ analysisId: string; personaId: string }>();
  const workspaceId = useCurrentWorkspaceId();
  const { data: analysis } = useQuery(analysisQuery(analysisId));
  const { data: persona, isLoading } = usePersonaById(
    personaId,
    workspaceId,
    analysis?.personaIds ?? undefined,
  );
  const startChat = useStartPersonaChat();
  const { onFocusGroup, onContentTest } = useStartNewActions(
    persona ? [persona.id] : [],
    analysisId,
  );

  // The route carries an id, and it can name either a persona the report was given or an
  // audience the report suggested. Suggested ones live in the report itself, so they are
  // answered from there and share this URL rather than needing one of their own.
  const suggested = analysis
    ? toAudienceDraftCards(analysis).find((card) => card.draftId === personaId)
    : undefined;
  if (suggested && analysisId) {
    // Answered even without a persona. A link to an audience still being built, or one whose
    // agents gave up, is reachable from a bookmark or by going back after a failure, and
    // falling through would look for a persona that this id can never name - a spinner that
    // never resolves.
    return <IQSuggestedAudienceDetail analysisId={analysisId} card={suggested} />;
  }

  if (isLoading || !persona) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const handleChat = () =>
    void startChat({
      personaId: persona.id,
      personaTitle: persona.personaTitle ?? undefined,
      segmentName: persona.segmentName ?? undefined,
    });

  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <SectionLayout
        variant="back"
        title={t('analysisResult.audienceProfiles.allAudienceProfiles')}
        onBack={() => void navigate('..', { relative: 'path' })}
        actions={
          <StartNewMenu
            onChat={handleChat}
            onFocusGroup={onFocusGroup}
            onContentTest={onContentTest}
          />
        }
      >
        <div className="overflow-auto bg-white rounded-lg">
          <PersonaDetailV2 persona={persona} />
        </div>
      </SectionLayout>
    </Suspense>
  );
};
